import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatBadge } from '@angular/material/badge'
import { MatIconButton } from '@angular/material/button'
import { MatCheckbox } from '@angular/material/checkbox'
import { MatOption } from '@angular/material/core'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatSelect, MatSelectTrigger } from '@angular/material/select'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import {
  ElderDialogService,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderSinglePaneWrapperComponent,
  ElderTabGroupRoutingDirective,
  ElderThemeDirective,
  ElderToastService,
  Pageable,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Duration } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, filter, mergeMap } from 'rxjs'
import { truthyPredicate } from '../../../../../functional.util'
import { IdValidator } from '../../../../features/validators/id-validator'
import { WorkItemMatcher } from '../../../matcher/work-item-matcher'
import { WorkItemMatcherDetailComponent } from '../../../matcher/work-item-matcher-detail/work-item-matcher-detail.component'
import { PackageType } from '../../../packages/types/package-type'
import { PackageTypeService } from '../../../packages/types/package-type.service'
import { ProcessingTypeUtil } from '../../processing-type/processing-type'
import { ProcessingTypeSelectDirective } from '../../processing-type/processing-type-select.directive'
import { LabelingSpecDetailComponent } from '../labeling-spec-detail/labeling-spec-detail.component'
import { OperationSegmentBrowserComponent } from '../operations/operation-segment-browser/operation-segment-browser.component'
import { ProcessPlanSchedulingComponent } from '../process-lead-time-spec-detail/process-plan-scheduling.component'
import { PackagingSpec, ProcessPlan } from '../process-plan'
import { ProcessPlanService } from '../process-plan.service'
import { DisplayPropertyBrowserComponent } from '../properties/display-property-browser/display-property-browser.component'
import { ScrapSpecBrowserComponent } from '../scrap/scrap-spec-browser/scrap-spec-browser.component'

@Component({
  selector: 'app-process-plan-detail',
  templateUrl: './process-plan-detail.component.html',
  styleUrls: ['./process-plan-detail.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneActionsComponent,
    ElderPaneSubtitleComponent,
    ElderPaneContentComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    ElderHeaderComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    MatFormField,
    MatLabel,
    MatInput,
    WorkItemMatcherDetailComponent,
    ElderSelectComponent,
    ProcessingTypeSelectDirective,
    MatCheckbox,
    OperationSegmentBrowserComponent,
    ProcessPlanSchedulingComponent,
    ScrapSpecBrowserComponent,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatSlideToggle,
    LabelingSpecDetailComponent,
    DisplayPropertyBrowserComponent,
    AsyncPipe,
    ElderTranslatePipe,
    MatBadge,
  ],
})
export class ProcessPlanDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  public readonly processPlan$ = new BehaviorSubject<ProcessPlan>(null)
  public readonly availablePackageTypes$ = new BehaviorSubject<PackageType[]>([])
  public readonly supportedPackageTypes$ = new BehaviorSubject<PackageType[]>([])

  private _creationMode: boolean

  public readonly idValidatorPattern = IdValidator.pattern

  private processPlanService = inject(ProcessPlanService)
  private packageTypeService = inject(PackageTypeService)
  private toastService = inject(ElderToastService)
  private dialogService = inject(ElderDialogService)

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.processPlan) {
      this.processPlan = this.initNewPlan()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set processPlan(plan: ProcessPlan) {
    if (plan) {
      this._creationMode = !plan.id

      if (!plan.workItemMatcher) {
        plan.workItemMatcher = new WorkItemMatcher()
      }

      if (!plan.packagingSpec) {
        plan.packagingSpec = new PackagingSpec(true)
      }

      this.initPackageTypes(plan.packagingSpec.supportedPackageTypes)

      this.processPlan$.next(plan)
    }
  }

  public get processPlan(): ProcessPlan {
    return this.processPlan$.getValue()
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public compareFn(type1: PackageType, type2: PackageType): boolean {
    return type1.id === type2.id
  }

  public onSupportedPackageTypesChanged(types: PackageType[]): void {
    if (!types) {
      types = []
    }
    this.supportedPackageTypes$.next(types)
  }

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public consumptionAndFinishedSupported(): boolean {
    return ProcessingTypeUtil.supportsConsumptionAndFinished(this.processPlan.processingType)
  }

  disablePackagingInputs(processPlan: ProcessPlan): boolean {
    return !processPlan.packagingSpec.packagingEnabled
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.processPlan.id) {
      this.processPlanService.findById(this.processPlan.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    const plan = this.processPlan

    plan.packagingSpec.supportedPackageTypes = this.supportedPackageTypes$
      .getValue()
      .map((type) => type.id)

    this.processPlanService.save(plan).subscribe({
      next: (updatedPlan) => {
        this.processPlan = updatedPlan
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.dialogService
      .showConfirm({
        title: 'messages.deleting_confirmation_title',
        message: 'messages.deleting_confirmation_msg',
      })
      .pipe(filter(truthyPredicate))
      .pipe(mergeMap((_) => this.processPlanService.delete(this.processPlan)))
      .subscribe({
        next: (_) => this.close.emit(),
        error: (err) => this.toastService.error('messages.deleting_failed', err),
      })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNewPlan(): ProcessPlan {
    return new ProcessPlan()
  }

  public updateReleaseAt(releaseAt: Duration) {
    this.processPlan = {
      ...this.processPlan,
      releaseAt: releaseAt.toString(),
    }
  }

  private initPackageTypes(supportedTypes: string[]) {
    this.packageTypeService.findAllPaged(new Pageable(0, 100)).subscribe((types) => {
      const availableTypes = types.content
      this.availablePackageTypes$.next(availableTypes)
      if (supportedTypes) {
        this.supportedPackageTypes$.next(
          supportedTypes
            .map((id) => availableTypes.find((t) => t.id === id))
            .sort((one, two) => (one.id > two.id ? -1 : 1))
        )
      }
    })
  }
}
